import React, { useEffect, useState } from "react";
import { InputText } from "primereact/inputtext";
import { ColorPicker } from "primereact/colorpicker";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { DweenTimeUnitEnum } from "utils/time-utils";
import { ChronoUnit } from "@energy-advice/easas-api";
import { TIME_UNITS_FROM_DAYS } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { DateLoadConfig } from "api/model/dashboard/DashboardTab";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { T } from "services/lang/LanguageService";
import { ICalendarHeatmapProps, ICommonCalendarHeatmapProps } from "../CalendarHeatmapTypes";

const defaultLoadConf = {
  alignStartTo: DweenTimeUnitEnum.YEARS,
  timeOffset: 0,
  timeOffsetUnit: DweenTimeUnitEnum.YEARS,
  lag: 0,
  lagUnit: DweenTimeUnitEnum.YEARS,
  lead: 1,
  leadUnit: DweenTimeUnitEnum.YEARS,
};

const getCurrentYearFirstDay = () => {
  const date = new Date();
  date.setMonth(0);
  date.setDate(1);
  return date;
};

const getCurrentYearLastDay = () => {
  const date = new Date();
  date.setMonth(11);
  date.setDate(31);
  return date;
};

const initialConfig: ICalendarHeatmapProps = {
  identifier: "",
  startDate: getCurrentYearFirstDay(),
  endDate: getCurrentYearLastDay(),
  minValue: undefined,
  maxValue: undefined,
  lowValueTitle: undefined,
  highValueTitle: undefined,
  lowValueColor: undefined,
  highValueColor: undefined,
  showDateSelection: true,
  staticMinMaxValues: false,
  staticMinMaxTitles: false,
  staticMinMaxColors: false,
  ignoreCurrentDay: false,
  useLoadConfig: false,
  loadConf: defaultLoadConf,
};

const CalendarHeatmapEditor = ({ config, onConfigChange }: ICommonCalendarHeatmapProps) => {
  const [currentConfig, setCurrentConfig] = useState<ICalendarHeatmapProps>({
    ...initialConfig,
    ...config,
  });
  const [dateFrom, setDateFrom] = useState<Date>(new Date(currentConfig.startDate));
  const [dateTo, setDateTo] = useState<Date>(new Date(currentConfig.endDate));
  useEffect(() => {
    if (JSON.stringify(config) !== JSON.stringify(currentConfig)) {
      onConfigChange(currentConfig);
    }
  }, [currentConfig, onConfigChange, config]);

  const handleColorChange = (field: keyof ICalendarHeatmapProps, color: any) => {
    setCurrentConfig(prevConfig => ({
      ...prevConfig,
      [field]: color || "",
    }));
  };

  const handleChronoUnitChange = (field: keyof DateLoadConfig, value: ChronoUnit) => {
    const convertedValue = String(value).toUpperCase();
    setCurrentConfig(prevConfig => ({
      ...prevConfig,
      loadConf: {
        ...prevConfig.loadConf,
        [field]: convertedValue,
      },
    }));
  };

  const handleNumberChange = (field: keyof DateLoadConfig, value: number | null) => {
    setCurrentConfig(prevConfig => ({
      ...prevConfig,
      loadConf: {
        ...prevConfig.loadConf,
        [field]: value ?? 0,
      },
    }));
  };
  return (
    <div>
      <div className="flex flex-column gap-4">
        <div className="flex flex-row gap-3">
          <CalendarWrapper
            value={dateFrom}
            onChange={e => {
              setDateFrom(e);
              setCurrentConfig(prevConfig => ({ ...prevConfig, startDate: e }));
            }}
            dateFormat="yy-mm-dd"
            className="h-3rem"
          />
          <CalendarWrapper
            value={dateTo}
            onChange={e => {
              setDateTo(e);
              setCurrentConfig(prevConfig => ({ ...prevConfig, endDate: e }));
            }}
            dateFormat="yy-mm-dd"
            className="h-3rem"
          />
          <div className="flex flex-column gap-1">
            <label htmlFor="showDateSelection">{T("chart-config.show-date-selection")}</label>
            <InputSwitch
              className="mt-auto mb-auto"
              checked={Boolean(currentConfig.showDateSelection)}
              onChange={e => setCurrentConfig(prevConfig => ({ ...prevConfig, showDateSelection: e.value }))}
            />
          </div>
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="identifier">{T("word.identifier")}</label>
          <DatasetDropdown
            value={currentConfig.identifier}
            onChange={e => setCurrentConfig(prevConfig => ({ ...prevConfig, identifier: e as string }))}
          />
        </div>
        <div className="flex flex-row gap-3">
          <div className="flex flex-column gap-1">
            <label htmlFor="staticMinMaxValues">Min/Max {T("word.values")}</label>
            <InputSwitch
              checked={currentConfig.staticMinMaxValues}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({
                  ...prevConfig,
                  staticMinMaxValues: value,
                  minValue: value ? prevConfig.minValue : undefined,
                  maxValue: value ? prevConfig.maxValue : undefined,
                }));
              }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="maxValue">Min/Max {T("word.title")}</label>
            <InputSwitch
              checked={currentConfig.staticMinMaxTitles}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({ ...prevConfig, staticMinMaxTitles: value }));
              }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="showDateSelection">Min/Max {T("word.colors")}</label>
            <InputSwitch
              checked={currentConfig.staticMinMaxColors}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({
                  ...prevConfig,
                  staticMinMaxColors: value,
                  lowValueColor: value ? prevConfig.lowValueColor : "#EF553B",
                  highValueColor: value ? prevConfig.highValueColor : "#636EFA",
                }));
              }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="showDateSelection">{T("label.calendar-heatmap.ignore-current-day")}</label>
            <InputSwitch
              checked={currentConfig.ignoreCurrentDay}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({
                  ...prevConfig,
                  ignoreCurrentDay: value,
                }));
              }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="showDateSelection">{T("action.use-load-config")}</label>
            <InputSwitch
              checked={Boolean(currentConfig.useLoadConfig)}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({
                  ...prevConfig,
                  useLoadConfig: value,
                }));
              }}
            />
          </div>
            <div className="flex flex-column gap-1">
            <label htmlFor="decimalPlaces">{T("config.decimal-places")}</label>
            <InputNumber
              value={currentConfig.decimalPlaces}
              onChange={e => {
                const { value } = e;
                setCurrentConfig(prevConfig => ({
                  ...prevConfig,
                  decimalPlaces: value ?? undefined,
                }));
              }}
            />

          </div>
        </div>
        <div className="flex flex-row gap-3">
          {currentConfig.staticMinMaxValues && (
            <div className="flex flex-column gap-1">
              <label htmlFor="minValue">Min {T("word.value")}</label>
              <InputNumber
                value={currentConfig.minValue}
                onChange={e => {
                  const value = e.value || 0;
                  setCurrentConfig(prevConfig => ({ ...prevConfig, minValue: value }));
                }}
                placeholder={`Min ${T("word.value")}`}
              />
            </div>
          )}
          {currentConfig.staticMinMaxTitles && (
            <div className="flex flex-column gap-1">
              <label htmlFor="">Min {T("word.value-title")}</label>
              <InputText
                value={currentConfig.lowValueTitle}
                onChange={e => {
                  const { value } = e.target;
                  setCurrentConfig(prevConfig => ({ ...prevConfig, lowValueTitle: value }));
                }}
                placeholder={`Low ${T("word.value-title")}`}
              />
            </div>
          )}
          {currentConfig.staticMinMaxColors && (
            <div className="flex flex-column gap-1">
              <label htmlFor="minValueColor">Min {T("word.value-color")}</label>
              <ColorPicker value={currentConfig.lowValueColor} onChange={e => handleColorChange("lowValueColor", e.value)} />
            </div>
          )}
        </div>
        <div className="flex flex-row gap-3">
          {currentConfig.staticMinMaxValues && (
            <div className="flex flex-column gap-1">
              <label htmlFor="maxValue">Max {T("word.value")}</label>
              <InputNumber
                value={currentConfig.maxValue}
                onChange={e => {
                  const value = e.value || 0;
                  setCurrentConfig(prevConfig => ({ ...prevConfig, maxValue: value }));
                }}
                placeholder={`Max ${T("word.value")}`}
              />
            </div>
          )}
          {currentConfig.staticMinMaxTitles && (
            <div className="flex flex-column gap-1">
              <label htmlFor="">Max {T("word.value-title")}</label>
              <InputText
                value={currentConfig.highValueTitle}
                onChange={e => {
                  const { value } = e.target;
                  setCurrentConfig(prevConfig => ({ ...prevConfig, highValueTitle: value }));
                }}
                placeholder={`High ${T("word.value-title")}`}
              />
            </div>
          )}
          {currentConfig.staticMinMaxColors && (
            <div className="flex flex-column gap-1">
              <label htmlFor="maxValueColor">Max {T("word.value-color")}</label>
              <ColorPicker value={currentConfig.highValueColor} onChange={e => handleColorChange("highValueColor", e.value)} />
            </div>
          )}
        </div>
        {currentConfig.useLoadConfig ? (
          <div className="flex flex-column gap-3">
            <div className="flex flex-column gap-2 w-2">
              <label htmlFor="alignStartTo">{T("load-conf.align-start-to")}</label>
              <TimeUnitDropdown
                id="alignStartTo"
                className="p-0"
                options={TIME_UNITS_FROM_DAYS}
                value={currentConfig.loadConf?.alignStartTo ?? DweenTimeUnitEnum.YEARS}
                onChange={e => {
                  handleChronoUnitChange("alignStartTo", e.value);
                }}
              />
            </div>
            <div className="flex flex-row gap-3">
              <div className="flex flex-column gap-2">
                <label htmlFor="timeOffset">{T("load-conf.time-offset")}</label>
                <div className="p-inputgroup">
                  <InputNumber
                    id="timeOffset"
                    value={(currentConfig.loadConf?.timeOffset as number) ?? 0}
                    onValueChange={e => {
                      handleNumberChange("timeOffset", e.value as number);
                    }}
                  />
                  <TimeUnitDropdown
                    id="timeOffsetUnit"
                    className="p-inputgroup-addon p-0"
                    options={TIME_UNITS_FROM_DAYS}
                    value={currentConfig.loadConf?.timeOffsetUnit ?? DweenTimeUnitEnum.YEARS}
                    onChange={e => {
                      handleChronoUnitChange("timeOffsetUnit", e.value);
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="lag">{T("load-conf.lag")}</label>
                <div className="p-inputgroup">
                  <InputNumber
                    id="lag"
                    value={(currentConfig.loadConf?.lag as number) ?? 0}
                    onValueChange={e => {
                      handleNumberChange("lag", e.value as number);
                    }}
                  />
                  <TimeUnitDropdown
                    id="lagUnit"
                    className="p-inputgroup-addon p-0"
                    options={TIME_UNITS_FROM_DAYS}
                    value={currentConfig.loadConf?.lagUnit ?? DweenTimeUnitEnum.YEARS}
                    onChange={e => {
                      handleChronoUnitChange("lagUnit", e.value);
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="lead">{T("load-conf.lead")}</label>
                <div className="p-inputgroup">
                  <InputNumber
                    id="lead"
                    value={(currentConfig.loadConf?.lead as number) ?? 0}
                    onValueChange={e => {
                      handleNumberChange("lead", e.value as number);
                    }}
                  />
                  <TimeUnitDropdown
                    id="leadUnit"
                    className="p-inputgroup-addon p-0"
                    options={TIME_UNITS_FROM_DAYS}
                    value={currentConfig.loadConf?.leadUnit ?? DweenTimeUnitEnum.YEARS}
                    onChange={e => {
                      handleChronoUnitChange("leadUnit", e.value);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-row gap-3">
            <CalendarWrapper
              value={dateFrom}
              onChange={e => {
                setDateFrom(e);
                setCurrentConfig(prevConfig => ({ ...prevConfig, startDate: e }));
              }}
              dateFormat="yy-mm-dd"
              className="h-3rem"
            />
            <CalendarWrapper
              value={dateTo}
              onChange={e => {
                setDateTo(e);
                setCurrentConfig(prevConfig => ({ ...prevConfig, endDate: e }));
              }}
              className="h-3rem"
              dateFormat="yy-mm-dd"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarHeatmapEditor;
